import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import Placeholder from "components/Layout/Placeholder";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { RecordPromptProps } from "components/Prompt/RecordPrompt";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { getRecordConfig, getWriteOnlyFieldsWithData } from "config/resourceUtils";
import objectTemplateAttributeConfig from "config/resources/object_template_attribute";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import {
    resourceDataFetchingSelector,
    resourceDataSelector,
} from "containers/Resources/Resources.selectors";
import { ResourceEditFields } from "containers/Resources/components/ResourceEditFields/ResourceEditFields";
import { valueAndSchema } from "containers/Resources/screens/ResourcesSingle/constants";
import {
    fetchSingleResourceData,
    filterObjectTemplateDataTypeList,
    getResourceDataInitialValues,
    newRecordId as newId,
    setValueDependingOnDataType,
    storeFormData,
    submitRelationshipRecordForm,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { updatedDiff } from "deep-object-diff";
import { useEffect, useMemo, useState } from "react";
import { Form as FinalForm, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import strings from "utils/strings";
import { getIri } from "utils/utils";
import "./RelationshipRecordForm.scss";

const ObjectTemplateAttributeSync = ({
    values,
    enabled,
}: {
    values: Record<string, unknown> | undefined;
    enabled: boolean;
}) => {
    useEffect(() => {
        if (!enabled || values == null) {
            return;
        }
        setValueDependingOnDataType(values);
        filterObjectTemplateDataTypeList((values.id as string | undefined) ?? "new");
    }, [enabled, values]);
    return null;
};

// The role_permission "Object template" refinement is only meaningful for
// CUSTOM_OBJECT grants (see role_permission.tsx — the picker is shown only for
// that record type). If the user changes the record type away from CUSTOM_OBJECT
// after picking a template, clear the now-hidden value so a stale (and
// backend-rejected) object_template_id is never submitted.
const RolePermissionScopeSync = ({
    values,
    form,
    enabled,
}: {
    values: Record<string, unknown> | undefined;
    form: { change: (name: string, value: unknown) => void };
    enabled: boolean;
}) => {
    const tableType = values?.table_type;
    const objectTemplateId = values?.object_template_id;
    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (objectTemplateId && tableType !== "CUSTOM_OBJECT") {
            form.change("object_template_id", null);
        }
    }, [enabled, tableType, objectTemplateId, form]);
    return null;
};

interface RelationshipRecordFormProps extends RecordPromptProps {
    isNested?: boolean;
    setNestedFormSaveData?: (value: any) => void;
    nestedFormSaveData?: { [key: string]: () => void };
    keepDirtyOnReinitialize?: boolean;
    defaultValues?: any;
    newRecordId?: string;
}

export const RelationshipRecordForm = ({
    data,
    onConfirm,
    onCancel,
    isNested,
    isOpen,
    setNestedFormSaveData,
    keepDirtyOnReinitialize = true,
    defaultValues,
    newRecordId = newId,
}: RelationshipRecordFormProps) => {
    const { resourceName, recordId, parentResourceType, parentResourceId, isReadOnly, isDisabled } =
        data;
    const resourceConfig = getRecordConfig(resourceName);

    const history = useHistory();
    const dispatch = useDispatch();
    const iri = getIri(resourceName, recordId);
    const resourceData = useSelector(resourceDataSelector(iri));
    const resourceDataFetching = useSelector(resourceDataFetchingSelector(recordId));
    const isFetching = resourceDataFetching.includes(iri);

    const writeOnlyFieldsWithData = getWriteOnlyFieldsWithData(resourceName, resourceData);

    const [isLoading, setLoading] = useState(true);

    const fetchResourceData = () => {
        if (!recordId) {
            return Promise.resolve();
        }

        return fetchSingleResourceData({ dispatch, resourceName, iri, recordId });
    };

    useEffect(() => {
        if (!isFetching) {
            setLoading(false);
        }
    }, [isFetching]);

    // fetch resource data if not already fetched
    useEffect(() => {
        if (isOpen && !resourceData && recordId) {
            setLoading(true);
            fetchResourceData().then(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, resourceData]);

    const isNew = useMemo(() => !recordId, [recordId]);
    const initialValues = useMemo(
        () => getResourceDataInitialValues(resourceConfig, resourceData),
        [resourceConfig, resourceData]
    );

    const [isSaveEnabledManually, setSaveEnabledManually] = useState(false);

    const submitRecordForm = (formData) =>
        submitRelationshipRecordForm({
            dispatch,
            formData,
            recordId,
            resourceName,
            additionalData: { parentResourceType, parentResourceId },
            resourceConfig,
            setNestedFormSaveData,
            newRecordId,
            additionalParams: { commit: false },
        });

    const isObjectTemplateAttribute = useMemo(
        () => objectTemplateAttributeConfig.resourceName === resourceName,
        [resourceName]
    );
    const CustomFormTag: keyof JSX.IntrinsicElements = `${isNested ? "div" : "form"}`;

    const { defaultSchemaValues } = useMatchingFields({ activeTab: resourceName });

    const defaultFormValues = useMemo(
        () => ({ ...defaultSchemaValues, ...(defaultValues || {}) }),
        [defaultSchemaValues, defaultValues]
    );

    return isLoading ? (
        <div className="spacer">
            <Placeholder />
        </div>
    ) : (
        <div className="RelationshipRecordForm">
            <FinalForm
                onSubmit={async (formData, form) => {
                    const changedValues = updatedDiff(form.getState().initialValues, formData);
                    if (formData[valueAndSchema]) {
                        changedValues[valueAndSchema] = formData[valueAndSchema];
                    }
                    return submitRecordForm({
                        ...(isNew ? defaultFormValues : {}),
                        ...changedValues,
                        id: formData.id,
                    });
                }}
                initialValues={initialValues}
                defaultValues={defaultFormValues}
                // special case for role_permission:
                // when initialValues are still fetching, the defaults are set
                // with keepDirtyOnReinitialize=false we can overwrite defaults when initialValues are already fetched
                keepDirtyOnReinitialize={keepDirtyOnReinitialize}
            >
                {({
                    handleSubmit,
                    pristine,
                    submitting,
                    hasValidationErrors,
                    form,
                    values,
                    submitSucceeded,
                    initialValues,
                }) => {
                    return (
                        <CustomFormTag
                            id="recordPromptForm"
                            className="recordPromptForm"
                            onSubmit={() => {
                                if (!pristine) handleSubmit();
                            }}
                        >
                            {!isNested && (
                                <div className="recordPrompt__header">
                                    {onCancel && (
                                        <IconButton
                                            className="recordPrompt__close"
                                            icon={IconCollection.close}
                                            onClick={onCancel}
                                        />
                                    )}
                                    <div className="recordPrompt__header-title">
                                        <span>{resourceConfig.label}</span>
                                        {resourceConfig.labelDocs ? (
                                            <DocsInfoTooltip
                                                title={resourceConfig.labelDocs.title}
                                                urlPath={resourceConfig.labelDocs.urlPath}
                                            />
                                        ) : null}
                                    </div>
                                    <div className="recordPrompt__header-actions">
                                        {onCancel && <CancelButton onClick={onCancel} />}
                                        <Button
                                            className="confirm-button"
                                            variation="primary"
                                            disabled={
                                                hasValidationErrors ||
                                                (isNew
                                                    ? pristine || submitting || submitSucceeded
                                                    : (pristine && !isSaveEnabledManually) ||
                                                      submitting)
                                            }
                                            onClick={() => {
                                                handleSubmit()
                                                    ?.then(() => fetchResourceData())
                                                    ?.then(() => {
                                                        onConfirm?.();
                                                    });
                                            }}
                                            label="Confirm"
                                        />
                                    </div>
                                </div>
                            )}
                            <FormSpy subscription={{ values: true }}>
                                {({ values: formValues }) => (
                                    <ObjectTemplateAttributeSync
                                        values={formValues}
                                        enabled={isObjectTemplateAttribute}
                                    />
                                )}
                            </FormSpy>
                            <RolePermissionScopeSync
                                values={values}
                                form={form}
                                enabled={resourceConfig.resourceName === "role_permission"}
                            />
                            <BlockingNavigationModal
                                navigate={history.push}
                                shouldBlockNavigation={!pristine}
                                titleText={strings.navigateAwayUnsavedPrompt}
                                submitForm={hasValidationErrors ? undefined : handleSubmit}
                                priority={NAVIGATION_PRIORITIES.NESTED_FORM}
                            />
                            <ResourceEditFields
                                formStructure={resourceConfig.form}
                                isDisabled={isDisabled}
                                isReadOnly={isReadOnly}
                                writeOnlyFieldsWithData={writeOnlyFieldsWithData}
                                recordId={recordId}
                                resourceTypeName={resourceName}
                                currentValues={values}
                                defaultValues={defaultFormValues}
                                enableSaveManually={setSaveEnabledManually}
                                additionalFieldProps={{
                                    hasInitialValuesLoaded:
                                        !recordId || initialValues !== undefined,
                                }}
                            />
                            {isNested && (
                                <div className="button-container">
                                    {onCancel && <CancelButton onClick={onCancel} />}
                                    <Button
                                        className="confirm-button"
                                        variation="primary"
                                        disabled={
                                            hasValidationErrors ||
                                            (isNew
                                                ? pristine || submitting || submitSucceeded
                                                : (pristine && !isSaveEnabledManually) ||
                                                  submitting)
                                        }
                                        onClick={() => {
                                            handleSubmit()
                                                ?.then(() => fetchResourceData())
                                                ?.then(() => {
                                                    onConfirm?.();
                                                });
                                        }}
                                        label="Confirm"
                                    />
                                </div>
                            )}
                            <FormSpy
                                onChange={({ pristine }) => {
                                    if (setNestedFormSaveData) {
                                        storeFormData({
                                            pristine,
                                            setNestedFormSaveData,
                                            recordId: recordId,
                                            form,
                                            isNew,
                                            newRecordId,
                                            fetchResourceData,
                                            onConfirm,
                                        });
                                    }
                                }}
                            />
                        </CustomFormTag>
                    );
                }}
            </FinalForm>
        </div>
    );
};
